import { m } from '@pricedrop/i18n';
import { Link, useNavigate } from '@tanstack/react-router';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';
import Mail from 'lucide-react/dist/esm/icons/mail';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { authClient } from '@/lib/auth-client';

interface LoginFormProps {
  redirectTo?: string;
  onSuccess?: () => void;
  compact?: boolean;
}

export function LoginForm({ redirectTo, onSuccess, compact = false }: LoginFormProps) {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [otp, setOtp] = React.useState('');
  const [step, setStep] = React.useState<'email' | 'otp'>('email');
  const [isLoading, setIsLoading] = React.useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isResending, setIsResending] = React.useState(false);
  const [resent, setResent] = React.useState(false);

  async function handleSignedIn() {
    if (onSuccess) {
      onSuccess();
      return;
    }

    await navigate({ to: redirectTo || '/' });
  }

  async function requestOtp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    const { error: requestError } = await authClient.emailOtp.sendVerificationOtp({
      email,
      type: 'sign-in',
    });

    setIsLoading(false);

    if (requestError) {
      setError(requestError.message || m.auth_errors_failedToSendOtp());
      return;
    }

    setStep('otp');
  }

  async function verifyOtp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    const { error: signInError } = await authClient.signIn.emailOtp({
      email,
      otp,
    });

    setIsLoading(false);

    if (signInError) {
      setError(signInError.message || m.auth_errors_failedToVerifyOtp());
      return;
    }

    await handleSignedIn();
  }

  async function signInWithGoogle() {
    setIsGoogleLoading(true);
    setError(null);

    // When on /login with no explicit redirect, default to home — avoids
    // Google OAuth redirecting back to the login page after authentication.
    const currentPath = window.location.pathname + window.location.search;
    const path = redirectTo || (currentPath.startsWith('/login') ? '/' : currentPath);
    const callbackURL = path.startsWith('http') ? path : `${window.location.origin}${path}`;
    const { error: socialError } = await authClient.signIn.social({
      provider: 'google',
      callbackURL,
    });

    setIsGoogleLoading(false);

    if (socialError) {
      setError(socialError.message || m.auth_errors_failedToSignInWithGoogle());
    }
  }

  async function resendOtp() {
    setIsResending(true);
    setError(null);

    const { error: resendError } = await authClient.emailOtp.sendVerificationOtp({
      email,
      type: 'sign-in',
    });

    setIsResending(false);

    if (resendError) {
      setError(resendError.message || m.auth_errors_failedToSendOtp());
      return;
    }

    setResent(true);
    setTimeout(() => setResent(false), 3000);
  }

  if (step === 'otp') {
    return (
      <div className="flex flex-col gap-6">
        <Card
          className={
            compact
              ? 'border-none shadow-none'
              : 'rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-800/60 dark:to-gray-700/60 border border-slate-200 dark:border-gray-600/50 shadow-lg shadow-blue-500/5 dark:shadow-blue-400/10'
          }
        >
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-slate-800 dark:text-gray-200">
              {m.auth_otp_title()}
            </CardTitle>
            <CardDescription className="text-slate-600 dark:text-gray-400">{`${m.auth_otp_description()} ${email}`}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={verifyOtp} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="otp">{m.auth_otp_label()}</Label>
                <InputOTP
                  id="otp"
                  maxLength={6}
                  value={otp}
                  onChange={setOtp}
                  containerClassName="w-full justify-center"
                  dir="ltr"
                  disabled={isLoading}
                  required
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <p className="min-h-5 text-sm text-destructive">{error}&nbsp;</p>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 border-0 text-white font-medium transition-all duration-300 hover:shadow-md hover:shadow-blue-500/20"
                disabled={isLoading || otp.length !== 6}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="me-2 size-4 animate-spin" />
                    {m.auth_otp_verifying()}
                  </>
                ) : (
                  <>
                    <CheckCircle className="me-2 size-4" />
                    {m.auth_otp_verifyButton()}
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full text-slate-600 dark:text-gray-400 hover:text-slate-800 dark:hover:text-gray-200"
                disabled={isLoading}
                onClick={() => {
                  setOtp('');
                  setStep('email');
                }}
              >
                {m.auth_otp_changeEmail()}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full text-slate-600 dark:text-gray-400 hover:text-slate-800 dark:hover:text-gray-200"
                disabled={isResending || resent || isLoading}
                onClick={resendOtp}
              >
                {isResending ? (
                  <>
                    <Loader2 className="me-2 size-4 animate-spin" />
                    {m.auth_otp_resending()}
                  </>
                ) : resent ? (
                  <>
                    <CheckCircle className="me-2 size-4" />
                    {m.auth_otp_resent()}
                  </>
                ) : (
                  m.auth_otp_resend()
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card
        className={
          compact
            ? 'border-none shadow-none'
            : 'rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-800/60 dark:to-gray-700/60 border border-slate-200 dark:border-gray-600/50 shadow-lg shadow-blue-500/5 dark:shadow-blue-400/10'
        }
      >
        <CardHeader className="pb-4">
          <CardTitle className="text-xl text-slate-800 dark:text-gray-200">
            {m.auth_login_title()}
          </CardTitle>
          <CardDescription className="text-slate-600 dark:text-gray-400">
            {m.auth_login_descriptionOtp()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={requestOtp} className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">{m.auth_login_emailLabel()}</Label>
              <div className="relative" dir="ltr">
                <Mail className="absolute start-3 top-2.5 size-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={m.auth_login_emailPlaceholder()}
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  className="ps-9"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading || isGoogleLoading}
                />
              </div>
            </div>
            <p className="min-h-5 text-sm text-destructive">{error}&nbsp;</p>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 border-0 text-white font-medium transition-all duration-300 hover:shadow-md hover:shadow-blue-500/20"
              disabled={isLoading || isGoogleLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="me-2 size-4 animate-spin" />
                  {m.auth_login_sendingOtp()}
                </>
              ) : (
                <>
                  <ShieldCheck className="me-2 size-4" />
                  {m.auth_login_sendOtp()}
                </>
              )}
            </Button>
          </form>

          <Separator className="my-4" />

          <Button
            type="button"
            variant="outline"
            className="w-full border-slate-200 dark:border-gray-600/50 hover:bg-slate-100 dark:hover:bg-gray-700/50 transition-all duration-300"
            onClick={signInWithGoogle}
            disabled={isLoading || isGoogleLoading}
          >
            {isGoogleLoading ? (
              <>
                <Loader2 className="me-2 size-4 animate-spin" />
                {m.auth_login_googleLoading()}
              </>
            ) : (
              <>
                <svg className="me-2 size-4" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                {m.auth_login_continueWithGoogle()}
              </>
            )}
          </Button>

          {!compact && (
            <>
              <Separator className="my-4" />
              <p className="text-center text-sm text-slate-600 dark:text-gray-400">
                {m.auth_signup_prompt()}{' '}
                <Link
                  to="/login"
                  search={(prev: Record<string, unknown>) => ({ ...prev, mode: 'signup' })}
                >
                  <span className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                    {m.auth_signup_cta()}
                  </span>
                </Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
